import lod_ from "lodash";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { styled, alpha } from "@mui/material/styles";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Checkbox,
	IconButton,
	Icon,
	Tooltip,
	Box,
	TextField,
	InputAdornment,
	Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import MDBox from "components/Basics/MDBox";
import { t } from "i18next";
import MDTypography from "components/Basics/MDTypography";
import MDInput from "components/Basics/MDInput";
import MDButton from "components/Basics/MDButton";
import { useLanguages } from "hooks/useLanguages";
import RowItem, {
	ALLOWED_FORMATS_FOR_HTML_SELECTOR,
	ALLOWED_FORMATS_FOR_OCR
} from "./components/RowItem";

const ROW_HEIGHT = 56;

// Styled components (same design as AssetTable)
const TableHeader = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	padding: theme.spacing(0.5, 1),
	borderBottom: "none",
	fontWeight: 100,
	background:
		theme.palette.mode === "dark"
			? alpha(theme.palette.grey[900], 0.5)
			: alpha(theme.palette.grey[100], 0.4),
	fontSize: "0.65rem",
	color: "light",
	position: "sticky",
	top: 0,
	zIndex: 1,
	boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.08)",
	borderRadius: "0.75rem 0.75rem 0 0"
}));

const HeaderCell = styled(Box)({
	display: "flex",
	alignItems: "center",
	justifyContent: "flex-start",
	position: "relative"
});

// Column widths
const COLUMNS = {
	checkbox: 50,
	name: "1.5fr",
	language: 100,
	htmlSelector: "1fr",
	ocr: 120,
	url: "1.5fr"
};

/**
 * Empty state component
 */
const EmptyState = () => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			height: "100%",
			minHeight: 250,
			gap: 1.5
		}}
	>
		<FolderOpenIcon sx={{ fontSize: 48, color: "text.disabled" }} />
		<Typography variant="h6" color="text.secondary" sx={{ opacity: 0.6 }}>
			{t("SOURCES.DISPLAY.noFiles", { defaultValue: "Aucun fichier trouvé" })}
		</Typography>
	</Box>
);

/**
 * Virtualized row renderer
 */
const VirtualizedRow = React.memo(({ index, style, data }) => {
	const {
		files,
		selectedUrls,
		onToggleSelection,
		onChangeRowItem,
		languages,
		languagesLoading,
		format
	} = data;
	const file = files[index];
	const isSelected = selectedUrls.has(file.url);

	return (
		<div style={style}>
			<RowItem
				file={file}
				isSelected={isSelected}
				onToggleSelection={onToggleSelection}
				onChange={onChangeRowItem}
				languages={languages || []}
				languagesLoading={languagesLoading}
				index={index}
				format={format}
			/>
		</div>
	);
});

VirtualizedRow.displayName = "VirtualizedRow";

/**
 * Display a table with all the files
 * User can select many files as he wants
 * @param {*} param0
 * @returns {JSX.Element} - StepSelect
 */
export default function AddAssetStepSelectFiles({
	format,
	skeleton,
	setSkeleton,
	validStep,
	onChange
}) {
	const [localFiles, setLocalFiles] = useState(skeleton.files || []);
	const [batchDialogOpen, setBatchDialogOpen] = useState(false);
	const [batchHtmlSelector, setBatchHtmlSelector] = useState("");
	const [searchQuery, setSearchQuery] = useState("");
	const { languages, loading: languagesLoading } = useLanguages();

	const selectedUrls = useMemo(() => new Set(skeleton.selectedRows), [skeleton.selectedRows]);

	// Filter files based on search query
	const filteredFiles = useMemo(() => {
		if (!searchQuery.trim()) {
			return localFiles;
		}
		const query = searchQuery.toLowerCase();
		return localFiles.filter(
			file => file.name?.toLowerCase().includes(query) || file.url?.toLowerCase().includes(query)
		);
	}, [localFiles, searchQuery]);

	// Fonction pour mettre à jour le htmlSelector de tous les fichiers en lot
	const handleBatchUpdateHtmlSelector = () => {
		const updatedFiles = localFiles.map(file => ({
			...file,
			metadatas: {
				...file.metadatas,
				htmlSelector: batchHtmlSelector
			}
		}));

		onChange(updatedFiles);
		setLocalFiles(updatedFiles);
		setBatchDialogOpen(false);
		setBatchHtmlSelector("");
	};

	// Memoized callbacks to prevent unnecessary re-renders
	const handleToggleSelection = useCallback(
		url => {
			setSkeleton(prev => {
				const isSelected = prev.selectedRows.includes(url);
				const newSelectedRows = isSelected
					? prev.selectedRows.filter(selectedUrl => selectedUrl !== url)
					: [...prev.selectedRows, url];

				return {
					...prev,
					selectedRows: newSelectedRows
				};
			});
		},
		[setSkeleton]
	);

	/**
	 * Toggle OCR for all files
	 */
	const toggleAllOCR = useCallback(() => {
		// Determine if we need to enable or disable OCR based on current state
		const allHaveOCR = localFiles.every(file => file.metadatas?.extractTextFromImage);
		const newOCRState = !allHaveOCR;

		const updatedFiles = localFiles.map(file => ({
			...file,
			metadatas: {
				...file.metadatas,
				extractTextFromImage: newOCRState
			}
		}));
		onChange(updatedFiles);
		setLocalFiles(updatedFiles);
	}, [localFiles, onChange]);

	/**
	 * Change handler for each row item
	 * @param {string} url - URL of the file to change
	 * @param {string} key - Key to change
	 * @param {any} value - New value
	 */
	const onChangeRowItem = useCallback(
		(url, key, value) => {
			setLocalFiles(prevFiles => {
				const updatedFiles = prevFiles.map(file => {
					if (file.url === url) {
						const newFile = { ...file };
						lod_.set(newFile, key, value);
						return newFile;
					}
					return file;
				});
				onChange(updatedFiles);
				return updatedFiles;
			});
		},
		[onChange]
	);

	const handleSelectAll = useCallback(
		e => {
			if (e.target.checked) {
				const allUrls = filteredFiles.map(file => file.url);
				setSkeleton(prev => ({ ...prev, selectedRows: allUrls }));
			} else {
				setSkeleton(prev => ({ ...prev, selectedRows: [] }));
			}
		},
		[filteredFiles, setSkeleton]
	);

	const allSelected =
		filteredFiles.length > 0 && filteredFiles.every(file => selectedUrls.has(file.url));
	const someSelected = filteredFiles.some(file => selectedUrls.has(file.url)) && !allSelected;

	useEffect(() => {
		if (lod_.isNil(skeleton) || lod_.isEmpty(skeleton)) {
			return;
		}

		if (skeleton.files && skeleton.files.length > 0) {
			setLocalFiles(skeleton.files);

			// Auto-select if only one file
			if (skeleton.files.length === 1 && skeleton.selectedRows.length === 0) {
				setSkeleton(prev => ({
					...prev,
					selectedRows: [skeleton.files[0].url]
				}));
			}
		}
		validStep();
	}, [skeleton.files]);

	useEffect(() => {
		setSkeleton(prev => ({
			...prev,
			selectedRows: []
		}));
	}, []);

	if (skeleton.files.length === 0) {
		return <EmptyState />;
	}

	return (
		<MDBox
			flex="1"
			sx={{
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				borderRadius: "0.75rem",
				backgroundColor: "background.paper",
				boxShadow: "0 20px 27px 0 rgba(0, 0, 0, 0.05)"
			}}
		>
			{/* Dialog pour la mise à jour en lot */}
			<Dialog
				open={batchDialogOpen}
				onClose={() => setBatchDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					<MDTypography variant="h6">{t("ASSETS.HTML_SELECTOR.editAllSelectors")}</MDTypography>
				</DialogTitle>
				<DialogContent>
					<MDBox pt={1}>
						<MDTypography variant="body2" mb={2}>
							{t("ASSETS.HTML_SELECTOR.editAllSelectorsDescription")}
						</MDTypography>
						<MDInput
							fullWidth
							value={batchHtmlSelector}
							onChange={e => setBatchHtmlSelector(e.target.value)}
							placeholder="Ex: article.content, .main-content, #content"
							label={t("ASSETS.HTML_SELECTOR.htmlSelector")}
						/>
					</MDBox>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setBatchDialogOpen(false)}>{t("SETTINGS.cancel")}</Button>
					<MDButton variant="gradient" color="info" onClick={handleBatchUpdateHtmlSelector}>
						{t("ASSETS.HTML_SELECTOR.applyToAll")}
					</MDButton>
				</DialogActions>
			</Dialog>

			{/* Search bar */}
			<Box
				sx={{ p: 0.75, borderBottom: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}` }}
			>
				<TextField
					size="small"
					placeholder={t("SOURCES.DISPLAY.search", { defaultValue: "Rechercher..." })}
					value={searchQuery}
					onChange={e => setSearchQuery(e.target.value)}
					fullWidth
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" color="action" />
							</InputAdornment>
						)
					}}
					sx={{
						"& .MuiOutlinedInput-root": {
							borderRadius: "0.5rem",
							backgroundColor: theme => alpha(theme.palette.grey[100], 0.3)
						},
						"& .MuiInputBase-input": {
							padding: "6px 8px",
							fontSize: "0.8rem"
						}
					}}
				/>
				<Box
					sx={{ mt: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}
				>
					<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
						{t("SOURCES.DISPLAY.filesCount", {
							count: filteredFiles.length,
							total: skeleton.files.length,
							defaultValue: `${filteredFiles.length} / ${skeleton.files.length} fichiers`
						})}
					</Typography>
				</Box>
			</Box>

			{/* Table Header */}
			<TableHeader>
				<Box sx={{ width: COLUMNS.checkbox, flexShrink: 0 }}>
					<Checkbox
						checked={allSelected}
						indeterminate={someSelected}
						onChange={handleSelectAll}
						size="small"
						sx={{
							p: 0.25,
							color: "inherit",
							"&.Mui-checked": { color: "inherit" },
							"&.MuiCheckbox-indeterminate": { color: "inherit" }
						}}
					/>
				</Box>

				<HeaderCell sx={{ flex: 1.5, pr: 1 }}>
					<MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "0.7rem" }}>
						{t("SOURCES.DISPLAY.name")}
					</MDTypography>
				</HeaderCell>

				<HeaderCell sx={{ width: COLUMNS.language, flexShrink: 0 }}>
					<MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "0.7rem" }}>
						{t("ASSETS.DETAILS.language")}
					</MDTypography>
				</HeaderCell>

				{ALLOWED_FORMATS_FOR_HTML_SELECTOR.includes(format) && (
					<HeaderCell sx={{ flex: 1 }}>
						<MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "0.7rem" }}>
							{t("ASSETS.HTML_SELECTOR.htmlSelector")}
						</MDTypography>
						<Tooltip title={t("ASSETS.HTML_SELECTOR.changeForAll")} placement="top">
							<IconButton
								size="small"
								sx={{ ml: 0.5, p: 0.25 }}
								color="info"
								onClick={() => setBatchDialogOpen(true)}
							>
								<Icon sx={{ fontSize: "1rem" }}>edit</Icon>
							</IconButton>
						</Tooltip>
					</HeaderCell>
				)}

				{ALLOWED_FORMATS_FOR_OCR.includes(format) && (
					<HeaderCell sx={{ width: COLUMNS.ocr, flexShrink: 0, justifyContent: "center" }}>
						<MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "0.7rem" }}>
							{t("SOURCES.EXTRACT.extractTextImageTitle")}
						</MDTypography>
						<Tooltip title={t("ASSETS.HTML_SELECTOR.changeForAll")} placement="top">
							<IconButton size="small" color="info" onClick={toggleAllOCR} sx={{ p: 0.25 }}>
								<Icon sx={{ fontSize: "1rem" }}>edit</Icon>
							</IconButton>
						</Tooltip>
					</HeaderCell>
				)}

				<HeaderCell sx={{ flex: 1.5, pl: 1 }}>
					<MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "0.7rem" }}>
						{t("SOURCES.DISPLAY.url")}
					</MDTypography>
				</HeaderCell>
			</TableHeader>

			{/* Virtualized List */}
			<Box sx={{ flex: 1, minHeight: 0 }}>
				{filteredFiles.length === 0 ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							minHeight: 150
						}}
					>
						<Typography variant="body2" color="text.secondary">
							{t("SOURCES.DISPLAY.noResults", {
								defaultValue: "Aucun résultat pour cette recherche"
							})}
						</Typography>
					</Box>
				) : (
					<AutoSizer>
						{({ height, width }) => (
							<List
								height={height}
								width={width}
								itemCount={filteredFiles.length}
								itemSize={ROW_HEIGHT}
								itemData={{
									files: filteredFiles,
									selectedUrls,
									onToggleSelection: handleToggleSelection,
									onChangeRowItem,
									languages,
									languagesLoading,
									format
								}}
								overscanCount={10}
							>
								{VirtualizedRow}
							</List>
						)}
					</AutoSizer>
				)}
			</Box>
		</MDBox>
	);
}
